import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
    Calendar, Lightning, Warning, Lock, CheckCircle, CurrencyInr, Plus,
    ShareNetwork, CaretDown, CaretUp, User,
    ArrowRight, PaperPlaneRight, UploadSimple, WarningCircle, Rocket,
} from "@phosphor-icons/react";
import {
    fmt, humanize, fmtRupees, fmtDate, EMPTY, PRIORITY_TONES,
    prettyValue, prettyField,
} from "./format";
import { CaseRoutingCards } from "./CaseRoutingCards";
import { DeliveryCard } from "./DeliveryCard";

/* ─────────────────────────────────────────────────────────────────── */
/* Primitives                                                          */
/* ─────────────────────────────────────────────────────────────────── */
function Card({ title, action, children, testId, className = "" }) {
    return (
        <div className={`rounded-2xl border border-border bg-card overflow-hidden ${className}`} data-testid={testId}>
            {title && (
                <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-secondary/30">
                    <h3 className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{title}</h3>
                    {action}
                </div>
            )}
            <div className="p-5">{children}</div>
        </div>
    );
}

function Row({ label, children, dataTestId }) {
    return (
        <div className="flex items-start gap-4 py-2" data-testid={dataTestId}>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground w-32 pt-0.5 shrink-0">{label}</div>
            <div className="text-sm flex-1 min-w-0">{children}</div>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────────────── */
/* CLINICAL SUMMARY (collapsible)                                      */
/* ─────────────────────────────────────────────────────────────────── */
const OBJECTIVE_TONES = {
    alignment:           "bg-sky-100 text-sky-700",
    bite_correction:     "bg-emerald-100 text-emerald-700",
    midline_correction:  "bg-violet-100 text-violet-700",
    spacing:             "bg-amber-100 text-amber-800",
    crowding:            "bg-rose-100 text-rose-700",
    cosmetic:            "bg-fuchsia-100 text-fuchsia-700",
    overjet:             "bg-teal-100 text-teal-700",
    overbite:            "bg-indigo-100 text-indigo-700",
};

function ClinicalSummary({ c }) {
    const [open, setOpen] = useState(true);
    const rx = c?.prescription || {};
    const clinical = c?.clinical || {};
    return (
        <Card
            title="Clinical Summary"
            testId="overview-clinical-summary"
            action={
                <button
                    onClick={() => setOpen((v) => !v)}
                    className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                    data-testid="clinical-summary-toggle"
                >
                    {open ? <CaretUp size={12} /> : <CaretDown size={12} />}
                    {open ? "Collapse" : "Expand"}
                </button>
            }
        >
            {open && (
                <div>
                    <div className="mb-3 -mt-1 p-2 rounded-lg bg-sky-50 border border-sky-200 text-[11px] text-sky-900 flex items-center gap-2" data-testid="clinical-source-banner">
                        <Lock size={11} className="text-sky-700" />
                        Sourced from doctor&apos;s case submission — read only
                    </div>
                    <Row label="Chief complaint" dataTestId="clinical-chief-complaint">
                        <div className="leading-relaxed whitespace-pre-wrap">{fmt(c?.diagnosis || clinical?.chief_complaint)}</div>
                    </Row>
                    <Row label="Esthetic goals" dataTestId="clinical-esthetic-goals">
                        {(rx.objectives && rx.objectives.length)
                            ? rx.objectives.map((o) => {
                                const tone = OBJECTIVE_TONES[o] || "bg-slate-100 text-slate-700";
                                return (
                                    <span key={o} className={`inline-block mr-1.5 mb-1 px-2 py-0.5 rounded-full text-xs font-medium ${tone}`}>
                                        {humanize(o)}
                                    </span>
                                );
                              })
                            : <span className="text-muted-foreground">{EMPTY}</span>}
                    </Row>
                    <Row label="Arch" dataTestId="clinical-arch">
                        <span className="font-medium">{prettyValue(c?.arches || clinical?.arch)}</span>
                    </Row>
                    <Row label="IPR preference" dataTestId="clinical-ipr">
                        <span className="font-medium">{prettyValue(rx.ipr_pref || clinical?.ipr_pref)}</span>
                    </Row>
                    <Row label="Attachments" dataTestId="clinical-attachments">
                        <span className="font-medium">{prettyField("attachment_pref", rx.attachment_pref || clinical?.attachments_pref)}</span>
                    </Row>
                    <Row label="Elastics" dataTestId="clinical-elastics">
                        <span className="font-medium">{prettyValue(rx.elastics || clinical?.elastics)}</span>
                    </Row>
                    <Row label="Extractions" dataTestId="clinical-extractions">
                        <span className="font-medium">{prettyValue(clinical?.extraction_plan || rx.extractions)}</span>
                    </Row>
                    <Row label="Do-not-touch" dataTestId="clinical-do-not-touch">
                        {(rx.do_not_touch_teeth && rx.do_not_touch_teeth.length)
                            ? rx.do_not_touch_teeth.map((t) => (
                                <span key={t} className="inline-block mr-1.5 mb-1 px-2 py-0.5 rounded bg-rose-50 text-rose-700 text-xs font-mono">{t}</span>
                              ))
                            : <span className="text-muted-foreground">None specified</span>}
                    </Row>
                </div>
            )}
        </Card>
    );
}

/* ─────────────────────────────────────────────────────────────────── */
/* BLOCKERS                                                            */
/* ─────────────────────────────────────────────────────────────────── */
/* iter101.7 — Blocker fix-action → click handler. Uses the shared
   `setTab` callback for tab jumps and a query-selector focus for
   in-tab fields so the admin never has to hunt for the offending
   field. */
const BLOCKER_ACTION_LABEL = {
    open_payments:              "Set quote",
    open_files:                 "Upload files",
    set_target_date:            "Set deadline",
    assign_production:          "Assign now",
    /* iter101.92 — Compliance-coach gate (Option 2) */
    assign_compliance_coach:    "Assign coach",
};

function runBlockerAction(code, { setTab } = {}) {
    if (code === "assign_production") {
        const el = document.querySelector('[data-testid="ops-production-select"]');
        el?.scrollIntoView({ block: "center", behavior: "smooth" });
        el?.focus();
        return;
    }
    if (code === "set_target_date") {
        const el = document.querySelector('[data-testid="ops-target-date"]');
        el?.scrollIntoView({ block: "center", behavior: "smooth" });
        el?.focus();
        return;
    }
    if (code === "open_payments") {
        const el = document.querySelector('[data-testid="overview-tab"] [data-testid^="payments"], [data-testid="overview-tab"] input[name="quote"]');
        el?.scrollIntoView({ block: "center", behavior: "smooth" });
        return;
    }
    if (code === "open_files") {
        setTab?.("fulfillment");
        return;
    }
    /* iter101.95 — Compliance coach row now lives inside the
       CaseRoutingCards' coach tile. Scroll to the tile, pulse the
       ring, and focus the assign select. */
    if (code === "assign_compliance_coach") {
        const card = document.querySelector('[data-testid="routing-card-coach"]');
        if (card) {
            card.scrollIntoView({ block: "center", behavior: "smooth" });
            card.classList.add("ring-4", "ring-rose-300", "rounded-2xl");
            setTimeout(() => card.classList.remove("ring-4", "ring-rose-300", "rounded-2xl"), 1800);
            const select = card.querySelector("select");
            select?.focus();
        }
        return;
    }
}

/* ─────────────────────────────────────────────────────────────────── */
/* OPERATIONS PANEL                                                    */
/* ─────────────────────────────────────────────────────────────────── */
/* iter101.7 — expanded so the Production Technician dropdown treats
   every post-approval status as "planning done". Previously
   `awaiting_production_assignment` (the state a case sits in AFTER the
   planner submits the MFG spec and BEFORE the admin picks a tech) was
   missing from this set, which meant the Ops panel rendered a locked
   "Available after doctor approval" tile — the exact UX confusion the
   admin flagged. */
const PLANNING_DONE_STATUSES = new Set([
    "approved", "approved_pending_mfg", "awaiting_production_assignment",
    "submitted_to_production", "assigned_to_production",
    "in_production", "shipped", "in_treatment",
    "treatment_started", "refinement_requested", "completed",
]);
/* States where the admin's action is required to pick a production
   technician. Any status outside this set either belongs before doctor
   approval OR is already in production / after. */
const AWAITING_TECH_ASSIGNMENT_STATUSES = new Set([
    "approved", "awaiting_production_assignment", "submitted_to_production",
]);
/* State where the doctor has approved but the planner still owes the
   MFG spec. Treat separately so the admin sees "waiting on planner"
   copy instead of a generic lock. */
const MFG_SPEC_PENDING_STATUSES = new Set(["approved_pending_mfg"]);

/* ─────────────────────────────────────────────────────────────────── */
/* ROUTE TO PRODUCTION — hero banner                                   */
/* iter101.7 — Between the moment the planner submits the MFG spec     */
/* (case flips to `awaiting_production_assignment`) and the moment the */
/* admin picks a production tech, there was no visible CTA telling the */
/* admin what to do — they had to hunt through the Operations grid.    */
/* This banner is the missing one-glance directive: it names the       */
/* current gate, lists blockers with fix buttons, and inlines the      */
/* tech-picker so assignment is a single click.                        */
/* ─────────────────────────────────────────────────────────────────── */
function RouteToProductionBanner({ c, blockers, productionTechs, reload, setTab }) {
    const status = c?.status;
    const alreadyAssigned = !!c?.production_tech_id;
    const isMfgPending = status === "approved_pending_mfg";
    const isAwaitingAssignment =
        status === "awaiting_production_assignment" ||
        status === "approved" ||
        status === "submitted_to_production";

    const [pickedTechId, setPickedTechId] = React.useState("");
    const [busy, setBusy] = React.useState(false);

    // Hide if nothing to prompt about
    if (alreadyAssigned) return null;
    if (!isMfgPending && !isAwaitingAssignment) return null;

    const hardBlockers = (blockers || []).filter((b) => b.severity === "blocker");
    const softBlockers = (blockers || []).filter((b) => b.severity !== "blocker");
    const readyToRoute = isAwaitingAssignment && hardBlockers.length === 0;

    const assign = async () => {
        if (!pickedTechId || busy) return;
        setBusy(true);
        try {
            await api.put(`/admin/cases/${c.id}/assign-production`, { production_tech_id: pickedTechId });
            const tech = productionTechs.find((t) => t.id === pickedTechId);
            toast.success(`Sent to production · ${tech?.name || "technician"}`);
            setPickedTechId("");
            reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't route to production");
        } finally {
            setBusy(false);
        }
    };

    // ── MFG-SPEC-PENDING variant (planner still authoring) ──
    if (isMfgPending) {
        return (
            <div
                className="rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-50/40 p-5 shadow-sm"
                data-testid="route-to-production-mfg-pending"
            >
                <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-700 grid place-items-center shrink-0">
                        <Lock size={20} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber-700">Next step · Planner</div>
                        <h3 className="text-lg font-bold tracking-tight text-foreground mt-0.5">Waiting on the planner&apos;s MFG spec</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            The doctor approved this case. The planner is finalising the manufacturing spec. Once submitted, this banner will unlock so you can route the case to a production technician.
                        </p>
                        {c?.planner_name && (
                            <p className="text-xs text-amber-800 mt-2">
                                <User size={11} className="inline -mt-0.5 mr-1" weight="bold" />
                                MFG spec owner: <span className="font-semibold">{c.planner_name}</span>
                            </p>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // ── AWAITING-ASSIGNMENT variant (admin's turn) ──
    return (
        <div
            className={`rounded-2xl border-2 p-5 shadow-sm transition ${readyToRoute
                ? "border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40"
                : "border-rose-200 bg-gradient-to-br from-rose-50 via-white to-rose-50/40"}`}
            data-testid="route-to-production-banner"
        >
            <div className="flex items-start gap-4">
                <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${readyToRoute
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-rose-100 text-rose-700"}`}
                >
                    <Rocket size={20} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className={`text-[10px] font-bold uppercase tracking-[0.16em] ${readyToRoute ? "text-emerald-700" : "text-rose-700"}`}>
                        Next step · Admin
                    </div>
                    <h3 className="text-lg font-bold tracking-tight text-foreground mt-0.5" data-testid="route-to-production-heading">
                        {readyToRoute
                            ? "Route this case to production"
                            : `Resolve ${hardBlockers.length} blocker${hardBlockers.length > 1 ? "s" : ""} to route this case`}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                        {readyToRoute
                            ? "The plan is doctor-approved and the MFG spec is locked. Pick a production technician to send this case to the manufacturing floor."
                            : "The plan is approved but production can't start until the items below are cleared."}
                    </p>

                    {/* Blockers as fix-now chips */}
                    {(hardBlockers.length > 0 || softBlockers.length > 0) && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {hardBlockers.map((b) => {
                                const cta = BLOCKER_ACTION_LABEL[b.fix_action] || "Fix";
                                return (
                                    <button
                                        key={b.code}
                                        onClick={() => runBlockerAction(b.fix_action, { setTab })}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-rose-600 text-white hover:bg-rose-700 transition"
                                        data-testid={`route-blocker-${b.code}`}
                                        title={b.message}
                                    >
                                        <WarningCircle size={12} weight="fill" />
                                        <span className="truncate max-w-[210px]">{b.message.split("—")[0].trim()}</span>
                                        <span className="opacity-90">·</span>
                                        <span>{cta}</span>
                                        <ArrowRight size={10} weight="bold" />
                                    </button>
                                );
                            })}
                            {softBlockers.map((b) => {
                                const cta = BLOCKER_ACTION_LABEL[b.fix_action] || "Fix";
                                return (
                                    <button
                                        key={b.code}
                                        onClick={() => runBlockerAction(b.fix_action, { setTab })}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-500 text-white hover:bg-amber-600 transition"
                                        data-testid={`route-blocker-${b.code}`}
                                        title={b.message}
                                    >
                                        <Warning size={12} weight="fill" />
                                        <span className="truncate max-w-[210px]">{b.message.split("—")[0].trim()}</span>
                                        <span className="opacity-90">·</span>
                                        <span>{cta}</span>
                                        <ArrowRight size={10} weight="bold" />
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Inline tech-picker + assign CTA */}
                    <div className="mt-4 flex flex-col sm:flex-row gap-2">
                        <select
                            value={pickedTechId}
                            onChange={(e) => setPickedTechId(e.target.value)}
                            disabled={!readyToRoute}
                            className={`flex-1 h-11 text-sm rounded-lg border-2 bg-background px-3 font-medium focus:outline-none focus:ring-2 ${readyToRoute
                                ? "border-emerald-400 focus:ring-emerald-400"
                                : "border-rose-200 bg-muted/20 text-muted-foreground cursor-not-allowed"}`}
                            data-testid="route-tech-select"
                        >
                            <option value="">Pick a production technician…</option>
                            {productionTechs.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.name}{typeof t.active_case_count === "number" ? ` · ${t.active_case_count} active` : ""}
                                </option>
                            ))}
                        </select>
                        <Button
                            onClick={assign}
                            disabled={!readyToRoute || !pickedTechId || busy}
                            className="h-11 px-5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold whitespace-nowrap disabled:bg-muted disabled:text-muted-foreground"
                            data-testid="route-assign-btn"
                            title={readyToRoute ? "Assign & send to production" : "Resolve blockers first"}
                        >
                            <PaperPlaneRight size={15} weight="fill" className="mr-1.5" />
                            {busy ? "Routing…" : "Send to Production"}
                        </Button>
                    </div>

                    <p className="text-[11px] text-muted-foreground mt-2">
                        Assigning a technician moves this case to <span className="font-bold text-foreground">IN PRODUCTION</span> and puts it on the tech&apos;s queue immediately.
                    </p>
                </div>
            </div>
        </div>
    );
}



export function OperationsPanel({ c, planners, qas, productionTechs, blockers, reload }) {
    // Doctor's planning choice — drives whether the COE is involved at all.
    //   planning_mode === "doctor" → doctor self-plans (no planner, no planning-type)
    //   planning_mode === "coe"    → Invisique COE plans on the doctor's behalf
    //                                → admin decides In-House vs Outsourced via planning_type
    const planningMode = c?.planning_mode || "coe";
    const isSelfPlan = planningMode === "doctor";
    // The Planning Type toggle locks once a planner has been assigned (any change
    // after that would orphan the planner's assignment).
    const planningTypeFrozen = !!c?.planner_id;

    // Priority is auto-set from the doctor's manufacturing-speed choice.
    //   pricing.priority_code: "standard" / "rush" / "stat"
    //   → admin badge:         "Normal"   / "Urgent" / "Rush"
    const priorityCode = (c?.pricing?.priority_code || "").toLowerCase();
    const priorityFromDoctor = priorityCode === "stat" ? "rush"
                             : priorityCode === "rush" ? "urgent"
                             : priorityCode === "standard" ? "normal"
                             : (c?.priority || "normal");
    const priorityTone = PRIORITY_TONES[priorityFromDoctor] || PRIORITY_TONES.normal;
    const priorityRawLabel = c?.pricing?.priority_label || (priorityCode ? humanize(priorityCode) : null);

    const plannerLocked = c?.planner_id && PLANNING_DONE_STATUSES.has(c?.status);
    const approvedOrLater = PLANNING_DONE_STATUSES.has(c?.status);

    // Production technician must be unlocked only after doctor approval AND zero blockers
    const hardBlockers = (blockers || []).filter((b) => b.severity === "blocker");
    const techBlocked = hardBlockers.length > 0;
    const productionReady = approvedOrLater && !techBlocked;

    // Pending-confirm modal state for technician assignment
    const [pendingTechId, setPendingTechId] = useState(null);

    const setPlanningType = async (val) => {
        if (planningTypeFrozen) return;
        try {
            await api.put(`/admin/cases/${c.id}/planning-type`, { planning_type: val });
            // Silent save — the toggle UI already reflects the change.
            // Only surface an error if the backend rejects it.
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update planning type"); }
    };
    const assign = async (kind, id) => {
        try {
            const body = kind === "planner" ? { planner_id: id }
                       : kind === "qa" ? { qa_id: id }
                       : { production_tech_id: id };
            const endpoint = kind === "production" ? "assign-production" : `assign-${kind}`;
            await api.put(`/admin/cases/${c.id}/${endpoint}`, body);
            const label = kind === "qa" ? "QA reviewer" : kind === "planner" ? "Planner" : "Production technician";
            toast.success(`${label} assigned`);
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    const setTarget = async (dateStr) => {
        try {
            await api.put(`/admin/cases/${c.id}/target-date`, { target_completion_date: dateStr });
            // Silent — the date picker already shows the new value.
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update target date"); }
    };

    const pendingTech = productionTechs.find((t) => t.id === pendingTechId);

    return (
        <Card title="Operations" testId="overview-operations">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* ── Row 1, col 1 — Planning Type (admin: In-house vs Outsourced) */}
                {isSelfPlan ? (
                    <div className="space-y-1.5" data-testid="ops-planning-type-selfplan">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 h-4">Planning type</label>
                        <div className="w-full h-10 px-3 rounded-lg border border-dashed border-border bg-muted/20 text-sm flex items-center text-muted-foreground italic">
                            Not applicable — doctor self-plans
                        </div>
                    </div>
                ) : (
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 h-4">Planning type</label>
                        <div className={`inline-flex w-full rounded-full border border-border bg-card overflow-hidden h-10 ${planningTypeFrozen ? "opacity-60" : ""}`}>
                            {["inhouse", "outsourced"].map((opt) => (
                                <button
                                    key={opt} type="button"
                                    disabled={planningTypeFrozen}
                                    onClick={() => setPlanningType(opt)}
                                    className={`flex-1 text-xs font-semibold transition ${c.planning_type === opt ? "bg-foreground text-white" : "text-muted-foreground hover:bg-secondary"} ${planningTypeFrozen ? "cursor-not-allowed" : ""}`}
                                    data-testid={`ops-planning-type-${opt}`}
                                >
                                    {opt === "inhouse" ? "In-house" : <><ShareNetwork size={11} className="inline mr-0.5" /> Outsourced</>}
                                </button>
                            ))}
                        </div>
                        {planningTypeFrozen && (
                            <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                                <Lock size={10} /> Locked after planner is assigned
                            </p>
                        )}
                    </div>
                )}

                {/* ── Row 1, col 2 — Priority (LOCKED, from doctor's manufacturing speed) */}
                <div className="space-y-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 h-4">
                        <Lightning size={11} /> Priority
                    </label>
                    <div
                        className={`w-full h-10 px-3 rounded-lg border border-border bg-muted/40 text-sm flex items-center justify-between gap-2`}
                        data-testid="ops-priority-locked"
                        title="Set by doctor at case submission. Contact support to change."
                    >
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${priorityTone.bg} ${priorityTone.text}`}>
                            {priorityTone.label}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground shrink-0">
                            {priorityRawLabel && <span className="font-mono">{priorityRawLabel}</span>}
                            <Lock size={11} />
                        </span>
                    </div>
                </div>

                {/* ── Row 2, col 1 — Planner (HIDDEN if self-plan) ────── */}
                {!isSelfPlan && (
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 h-4">Planner</label>
                        {plannerLocked ? (
                            <div className="w-full h-10 px-3 rounded-lg border border-border bg-muted/40 text-sm flex items-center justify-between gap-2" data-testid="ops-planner-locked">
                                <span className="font-medium truncate">{c.planner_name || EMPTY}</span>
                                <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground uppercase tracking-wider font-bold">
                                    <Lock size={10} /> Done
                                </span>
                            </div>
                        ) : (
                            <select
                                value={c.planner_id || ""}
                                onChange={(e) => e.target.value && assign("planner", e.target.value)}
                                className="w-full h-10 text-sm rounded-lg border border-border bg-background px-2"
                                data-testid="ops-planner-select"
                            >
                                <option value="">{c.planner_name || "Select planner…"}</option>
                                {planners.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.name}{typeof p.active_case_count === "number" ? ` · ${p.active_case_count} active` : ""}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                )}
                {isSelfPlan && (
                    <div className="space-y-1.5" data-testid="ops-planner-skipped">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 h-4">Planner</label>
                        <div className="w-full h-10 px-3 rounded-lg border border-dashed border-border bg-muted/20 text-sm flex items-center text-muted-foreground italic">
                            Not needed — doctor self-plans
                        </div>
                    </div>
                )}

                {/* iter101.50 — QA Reviewer field HIDDEN from the active
                    admin flow. Product decision to retire QA from the
                    pipeline for now (we may bring it back later). The
                    row is rendered only when a case already carries a
                    `qa_id` (legacy assignment) so admin can still see
                    the historical reviewer, but the "required" nudge +
                    the picker are removed. */}
                {c?.qa_id ? (
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 h-4">
                            QA reviewer <span className="text-muted-foreground/60">· historical</span>
                        </label>
                        <div className="w-full h-10 px-3 rounded-lg border border-border bg-muted/20 text-sm flex items-center text-foreground/80">
                            {c.qa_name || "QA reviewer"}
                        </div>
                    </div>
                ) : null}

                {/* ── Row 3, col 1 — Production Technician ────────────── */}
                <div className="space-y-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Production technician</label>
                    {c?.production_tech_id ? (
                        <div className="w-full h-10 px-3 rounded-lg border border-emerald-200 bg-emerald-50/60 text-sm flex items-center justify-between gap-2" data-testid="ops-production-assigned">
                            <span className="font-medium text-emerald-900 truncate">{c.production_tech_name || "Production tech"}</span>
                            <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 uppercase tracking-wider font-bold">
                                <CheckCircle size={11} weight="fill" /> Assigned
                            </span>
                        </div>
                    ) : MFG_SPEC_PENDING_STATUSES.has(c?.status) ? (
                        <div className="w-full h-10 px-3 rounded-lg border border-dashed border-amber-300 bg-amber-50/40 text-sm flex items-center text-amber-800" data-testid="ops-production-locked-mfg">
                            <Lock size={11} className="mr-1.5" /> Waiting for planner · MFG spec pending
                        </div>
                    ) : !AWAITING_TECH_ASSIGNMENT_STATUSES.has(c?.status) ? (
                        <div className="w-full h-10 px-3 rounded-lg border border-dashed border-border bg-muted/30 text-sm flex items-center text-muted-foreground" data-testid="ops-production-locked-approval">
                            <Lock size={11} className="mr-1.5" /> Available after doctor approval
                        </div>
                    ) : techBlocked ? (
                        <div
                            className="w-full h-10 px-3 rounded-lg border border-dashed border-rose-200 bg-rose-50/40 text-sm flex items-center text-rose-700"
                            data-testid="ops-production-locked-blockers"
                            title="Resolve all warnings before assigning a technician"
                        >
                            <Lock size={11} className="mr-1.5" /> Resolve warnings to enable
                        </div>
                    ) : (
                        <select
                            value=""
                            onChange={(e) => e.target.value && setPendingTechId(e.target.value)}
                            className="w-full h-10 text-sm rounded-lg border-2 border-emerald-400 bg-emerald-50/30 px-2 font-medium text-emerald-900 focus:ring-2 focus:ring-emerald-400 focus:outline-none"
                            data-testid="ops-production-select"
                        >
                            <option value="">Pick a technician to send to production…</option>
                            {productionTechs.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.name}{typeof t.active_case_count === "number" ? ` · ${t.active_case_count} active` : ""}
                                </option>
                            ))}
                        </select>
                    )}
                    {productionReady && !c?.production_tech_id && (
                        <p className="text-[10px] text-emerald-700 font-medium flex items-center gap-1">
                            <ArrowRight size={10} weight="bold" />
                            Assigning a technician moves this case to <span className="font-bold">IN PRODUCTION</span>.
                        </p>
                    )}
                </div>

                {/* ── Row 3, col 2 — Target Completion ────────────────── */}
                <div className="space-y-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                        <Calendar size={11} /> Target completion
                    </label>
                    <input
                        type="date"
                        value={(c.target_completion_date || "").slice(0, 10)}
                        onChange={(e) => setTarget(e.target.value)}
                        className="w-full h-10 text-sm rounded-lg border border-border bg-background px-2"
                        data-testid="ops-target-date"
                    />
                </div>
            </div>

            {/* ── Confirmation modal for technician assignment ────────── */}
            <Dialog open={!!pendingTechId} onOpenChange={(o) => !o && setPendingTechId(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Send to production?</DialogTitle>
                        <DialogDescription>
                            Assigning <span className="font-bold text-foreground">{pendingTech?.name || "this technician"}</span> as Production Technician will move this case to <span className="font-bold text-foreground">IN PRODUCTION</span>. This kicks off the manufacturing workflow and is logged in the audit trail.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPendingTechId(null)} data-testid="confirm-production-cancel">Cancel</Button>
                        <Button
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={async () => {
                                const id = pendingTechId;
                                setPendingTechId(null);
                                await assign("production", id);
                            }}
                            data-testid="confirm-production-submit"
                        >
                            Confirm — start production
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}

/* ─────────────────────────────────────────────────────────────────── */
/* PAYMENT WIDGET                                                      */
/* ─────────────────────────────────────────────────────────────────── */
function PaymentsCard({ c, reload }) {
    const [payments, setPayments] = useState([]);
    const [quoteEdit, setQuoteEdit] = useState(false);
    const [quoteVal, setQuoteVal] = useState("");
    const [addOpen, setAddOpen] = useState(false);
    const [addAmt, setAddAmt] = useState("");
    const [addOn, setAddOn] = useState(new Date().toISOString().slice(0, 10));
    const [busy, setBusy] = useState(false);

    const fetchPayments = () => {
        api.get(`/cases/${c.id}/payments`)
            .then((r) => setPayments(r.data?.payments || []))
            .catch(() => {});
    };
    useEffect(() => { fetchPayments(); }, [c.id]);

    const saveQuote = async () => {
        const n = Number(quoteVal);
        if (Number.isNaN(n) || n < 0) { toast.error("Enter a valid amount"); return; }
        setBusy(true);
        try {
            await api.put(`/admin/cases/${c.id}/quote`, { patient_quote_total: n });
            toast.success("Quote updated");
            setQuoteEdit(false);
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };
    const addPayment = async () => {
        const n = Number(addAmt);
        if (!n || n <= 0) { toast.error("Amount must be > 0"); return; }
        setBusy(true);
        try {
            await api.post(`/cases/${c.id}/payments`, {
                amount: n, mode: "other", paid_on: addOn, kind: "regular",
            });
            toast.success("Payment added");
            setAddAmt("");
            setAddOpen(false);
            fetchPayments();
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    // Pricing breakdown sourced from the doctor's submission (case.pricing).
    // The admin can override the total via `patient_quote_total`; otherwise
    // we surface `pricing.final_charge` (lab + priority fee) as the quote.
    const pr = c?.pricing || {};
    const doctorTotal = Number(pr.final_charge || 0);
    const adminOverride = c?.patient_quote_total !== null && c?.patient_quote_total !== undefined && c?.patient_quote_total !== "";
    const quoteTotal = adminOverride ? Number(c.patient_quote_total || 0) : doctorTotal;
    const labCharge = Number(pr.lab_charge || 0);
    const priorityFee = Number(pr.priority_fee || 0);
    const discountAmt = Number(pr.discount_amount || 0);
    const tierName = pr.tier_name || c?.tier_label || c?.tier_code;
    const priorityLabel = pr.priority_label || (c?.priority && c.priority !== "normal" ? humanize(c.priority) : null);
    const advance = Number(pr.advance_amount || 0);

    // Advance committed by the doctor on submission. If a payment with
    // kind="advance" already exists, we mark this as already-received and
    // hide the CTA. Falls back to advance_pct × doctor total when only the
    // percentage was set.
    const advanceFromAmt = Number(pr.advance_amount || 0);
    const advancePct = Number(pr.advance_pct || 0);
    const advanceExpected = advanceFromAmt > 0
        ? advanceFromAmt
        : (advancePct > 0 && doctorTotal > 0 ? Math.round((advancePct / 100) * doctorTotal) : 0);
    const advancePayment = payments.find((p) => p.kind === "advance" && !p.is_deleted);
    const advanceReceived = !!advancePayment;

    const markAdvanceReceived = async () => {
        if (advanceExpected <= 0) { toast.error("No advance amount on this case"); return; }
        setBusy(true);
        try {
            await api.post(`/cases/${c.id}/payments`, {
                amount: advanceExpected,
                mode: "other",
                kind: "advance",
                note: `Advance per doctor's submission${advancePct > 0 ? ` (${advancePct}% upfront)` : ""}`,
            });
            toast.success("Advance recorded");
            fetchPayments();
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    const paidTotal = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
    const balance = Math.max(0, quoteTotal - paidTotal);
    const status = quoteTotal === 0 ? "unquoted"
                 : paidTotal === 0 ? "unpaid"
                 : paidTotal >= quoteTotal ? "paid"
                 : "partial";
    const statusTone = {
        unquoted: "bg-rose-100 text-rose-800",
        unpaid:   "bg-rose-100 text-rose-800",
        partial:  "bg-amber-100 text-amber-800",
        paid:     "bg-emerald-100 text-emerald-800",
    }[status];

    return (
        <Card
            title="Payments"
            testId="overview-payments"
            action={
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusTone}`} data-testid="payments-status-badge">
                    {humanize(status)}
                </span>
            }
        >
            {/* Doctor's committed pricing breakdown (read-only) */}
            {(tierName || labCharge > 0 || priorityFee > 0) && (
                <div className="mb-3 pb-3 border-b border-border space-y-1.5">
                    <div className="mb-1 p-2 rounded-lg bg-sky-50 border border-sky-200 text-[11px] text-sky-900 flex items-center gap-2" data-testid="payments-source-banner">
                        <Lock size={11} className="text-sky-700" />
                        Pricing sourced from doctor&apos;s case submission
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Doctor&apos;s submission</span>
                        {adminOverride && (
                            <span className="text-[9px] uppercase tracking-wider text-amber-700 font-bold" title="Admin has overridden the quote">Admin override</span>
                        )}
                    </div>
                    {tierName && (
                        <div className="flex items-center justify-between text-sm" data-testid="payments-tier-line">
                            <span className="text-muted-foreground">Treatment tier</span>
                            <span className="font-semibold">{tierName}</span>
                        </div>
                    )}
                    {labCharge > 0 && (
                        <div className="flex items-center justify-between text-sm" data-testid="payments-lab-line">
                            <span className="text-muted-foreground">Lab charge</span>
                            <span className="font-mono">{fmtRupees(labCharge)}</span>
                        </div>
                    )}
                    {priorityFee > 0 && (
                        <div className="flex items-center justify-between text-sm" data-testid="payments-priority-line">
                            <span className="text-muted-foreground">Priority surcharge{priorityLabel ? ` · ${priorityLabel}` : ""}</span>
                            <span className="font-mono text-amber-700">+ {fmtRupees(priorityFee)}</span>
                        </div>
                    )}
                    {discountAmt > 0 && (
                        <div className="flex items-center justify-between text-sm" data-testid="payments-discount-line">
                            <span className="text-muted-foreground">Discount{pr.promo_code ? ` · ${pr.promo_code}` : ""}</span>
                            <span className="font-mono text-emerald-700">− {fmtRupees(discountAmt)}</span>
                        </div>
                    )}
                    {doctorTotal > 0 && (
                        <div className="flex items-center justify-between text-sm pt-1.5 border-t border-border/60" data-testid="payments-doctor-total">
                            <span className="font-bold text-foreground">Doctor&apos;s total</span>
                            <span className="font-mono font-bold">{fmtRupees(doctorTotal)}</span>
                        </div>
                    )}
                    {advanceExpected > 0 && (
                        <div
                            className={`mt-2 p-2.5 rounded-lg border flex items-center justify-between gap-3 ${advanceReceived
                                ? "border-emerald-200 bg-emerald-50"
                                : "border-amber-200 bg-amber-50"}`}
                            data-testid="payments-advance-row"
                        >
                            <div className="min-w-0">
                                <div className={`text-[10px] uppercase tracking-wider font-bold ${advanceReceived ? "text-emerald-700" : "text-amber-800"}`}>
                                    Advance committed {advancePct > 0 ? `(${advancePct}%)` : ""}
                                </div>
                                <div className="text-sm font-mono font-bold mt-0.5">{fmtRupees(advanceExpected)}</div>
                                {advanceReceived && advancePayment?.paid_on && (
                                    <div className="text-[10px] text-emerald-700 mt-0.5">Received {fmtDate(advancePayment.paid_on)}</div>
                                )}
                            </div>
                            {advanceReceived ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider shrink-0" data-testid="payments-advance-received-badge">
                                    <CheckCircle size={10} weight="fill" /> Received
                                </span>
                            ) : (
                                <button
                                    onClick={markAdvanceReceived}
                                    disabled={busy}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold shrink-0 disabled:opacity-50"
                                    data-testid="payments-mark-advance-received"
                                >
                                    Mark received
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Stat trio */}
            <div className="grid grid-cols-3 gap-3 mb-4">
                <div>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">Quote</div>
                    {quoteEdit ? (
                        <input
                            type="number" min="0" value={quoteVal}
                            onChange={(e) => setQuoteVal(e.target.value)}
                            className="w-full h-9 text-sm font-bold rounded border border-border bg-background px-2 mt-1"
                            data-testid="payments-quote-input"
                        />
                    ) : (
                        <button
                            onClick={() => { setQuoteVal(String(quoteTotal || "")); setQuoteEdit(true); }}
                            className="text-left w-full"
                            data-testid="payments-quote-edit"
                        >
                            <div className="text-base font-bold tabular-nums">{fmtRupees(quoteTotal)}</div>
                            <div className="text-[10px] text-primary mt-0.5 hover:underline">{adminOverride ? "Edit override" : "Override"}</div>
                        </button>
                    )}
                </div>
                <div>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">Paid</div>
                    <div className="text-base font-bold tabular-nums text-emerald-700 mt-1">{fmtRupees(paidTotal)}</div>
                    <div className="text-[10px] text-muted-foreground">{payments.length} payment{payments.length !== 1 ? "s" : ""}</div>
                </div>
                <div>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">Balance</div>
                    <div className="text-base font-bold tabular-nums mt-1">{fmtRupees(balance)}</div>
                </div>
            </div>

            {quoteEdit && (
                <div className="flex gap-2 mb-3">
                    <button onClick={saveQuote} disabled={busy} className="flex-1 py-1.5 text-xs font-bold rounded-lg bg-primary text-white" data-testid="payments-quote-save">Save quote</button>
                    <button onClick={() => { setQuoteEdit(false); setQuoteVal(String(quoteTotal || "")); }} className="flex-1 py-1.5 text-xs font-bold rounded-lg border border-border">Cancel</button>
                </div>
            )}

            {quoteTotal === 0 && !quoteEdit && (
                <div className="p-2 rounded-lg border border-rose-200 bg-rose-50 text-rose-900 text-xs flex items-center gap-2 mb-3" data-testid="payments-alert-unquoted">
                    <CurrencyInr size={14} weight="bold" />
                    Case cannot proceed to production with ₹0 quoted amount.
                </div>
            )}

            {/* Recent payments list */}
            {payments.length > 0 && (
                <div className="mb-3 space-y-1.5">
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">Recent payments</div>
                    {payments.slice(0, 4).map((p) => (
                        <div key={p.id} className="flex items-center justify-between text-xs py-1 border-b border-border/40 last:border-0">
                            <span className="text-muted-foreground inline-flex items-center gap-1">
                                {fmtDate(p.paid_on)}
                                {p.kind === "advance" && (
                                    <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[9px] font-bold uppercase tracking-wider">Advance</span>
                                )}
                            </span>
                            <span className="font-mono font-semibold">{fmtRupees(p.amount)}</span>
                        </div>
                    ))}
                    {payments.length > 4 && (
                        <div className="text-[10px] text-muted-foreground text-center pt-1">+ {payments.length - 4} more</div>
                    )}
                </div>
            )}

            {/* Add payment */}
            {addOpen ? (
                <div className="p-3 rounded-lg border border-border bg-secondary/30 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                        <input
                            type="number" min="1" placeholder="Amount"
                            value={addAmt}
                            onChange={(e) => setAddAmt(e.target.value)}
                            className="h-9 text-sm rounded border border-border bg-background px-2"
                            data-testid="payments-add-amount"
                        />
                        <input
                            type="date"
                            value={addOn}
                            onChange={(e) => setAddOn(e.target.value)}
                            className="h-9 text-sm rounded border border-border bg-background px-2"
                            data-testid="payments-add-date"
                        />
                    </div>
                    <div className="flex gap-2">
                        <button onClick={addPayment} disabled={busy} className="flex-1 h-9 rounded bg-primary text-white text-xs font-bold" data-testid="payments-add-submit">Record payment</button>
                        <button onClick={() => setAddOpen(false)} className="flex-1 h-9 rounded border border-border text-xs font-bold">Cancel</button>
                    </div>
                </div>
            ) : (
                <button
                    onClick={() => setAddOpen(true)}
                    className="w-full inline-flex items-center justify-center gap-1 h-9 rounded-lg border border-dashed border-border text-xs font-bold text-muted-foreground hover:bg-secondary transition"
                    data-testid="payments-add-toggle"
                >
                    <Plus size={11} weight="bold" /> Record payment
                </button>
            )}
        </Card>
    );
}


export function OverviewTab({ c, blockers, planners, qas, productionTechs, reload, setTab }) {
    /* iter101.95 — Single-column layout. The right sidebar
       (`TeamAndDeliveryPanel`) has been retired. The 3-card
       `CaseRoutingCards` now owns every admin-side assignment
       mutation (Planner / Production / Compliance Coach) and the
       Delivery contract (Planning type · Priority · Target date)
       lives in the dedicated `DeliveryCard` right below it.

       iter101.96 — `StageHeroCard` also retired. Stage/status is
       already conveyed by the ClinicalPathwayStepper at the top of
       the page, the status pill in CaseHeader, and the assignee
       avatars inside CaseRoutingCards, so the hero was pure noise
       for every "case moving forward on autopilot" state.
       `RouteToProductionBanner` remains — it's the actionable hero
       for the routing-decision states that need admin's attention. */
    return (
        <div className="space-y-5" data-testid="overview-tab">
            <RouteToProductionBanner
                c={c}
                blockers={blockers}
                productionTechs={productionTechs}
                reload={reload}
                setTab={setTab}
            />
            <CaseRoutingCards
                c={c}
                blockers={blockers}
                planners={planners}
                productionTechs={productionTechs}
                reload={reload}
            />
            <DeliveryCard c={c} reload={reload} />
            <PaymentsCard c={c} reload={reload} />
            <ClinicalSummary c={c} />
        </div>
    );
}
